import React from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { DownloadBox } from '../Components/DownloadBox';

const themeBreakpoint = 'sm';

const useStyles = makeStyles((theme) => ({
  frontBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down(themeBreakpoint)]: {
      flexDirection: 'column',
    },
  },
  punchlineContainer: {
    flexGrow: 1,
    [theme.breakpoints.down(themeBreakpoint)]: {
      width: '100%'
    },
  },
  subtitle1: {
    marginLeft: theme.spacing(5),
    textAlign: 'left',
    [theme.breakpoints.down(themeBreakpoint)]: {
      marginLeft: 0,
    },
  },
  subtitle2: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(5),
    textAlign: 'right',
  },
  downloadBlock: {
    marginLeft: theme.spacing(5),
    minWidth: theme.spacing(40),
    [theme.breakpoints.down(themeBreakpoint)]: {
      marginLeft: 0,
    },
  },
  featurePage: {
    maxWidth: '70%',
    margin: 'auto',
    paddingBottom: theme.spacing(5),
    [theme.breakpoints.down(themeBreakpoint)]: {
      maxWidth: '100%',
    },
  },
  featureBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    '& + &': {
      marginTop: theme.spacing(20),
    },
    '& > * + *': {
      marginLeft: theme.spacing(5),
    },
    [theme.breakpoints.down(themeBreakpoint)]: {
      flexDirection: 'column',
      '& > * + *': {
        marginLeft: 0,
      },
      '&$rl': {
        flexDirection: 'column-reverse',
      }
    },
  },
  featureImg: {
    userSelect: 'none',
    userDrag: 'none',
    maxWidth: '100%',
    [theme.breakpoints.down(themeBreakpoint)]: {
      marginTop: theme.spacing(2),
    },
  },
  featureTitle: {
    marginBottom: theme.spacing(2),
  },
  featureTextBlock: {
  },
  featureText: {
    textAlign: 'justify',
  },
  rl: {

  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  ankiDescription: {
    textAlign: 'justify',
    marginTop: theme.spacing(5),
    color: theme.palette.text.secondary,
    quotes: '"“" "”"',
    '& q::before': {
      fontWeight: 'bold',
    },
    '& q::after': {
      fontWeight: 'bold',
    },
  }
}));

export const HomePage = (): JSX.Element => {
  const classes = useStyles();
  return (
    <div>
      <div className={classes.frontBlock}>
        <div className={classes.punchlineContainer}>
          <Typography variant="h5" component="h2" className={classes.subtitle1}>
            Learn any language by watching the best movies.
          </Typography>
          <Typography variant="h5" component="h2" className={classes.subtitle2}>
            Translate and export to Anki flashcards on&#8209;the&#8209;fly.
          </Typography>
        </div>
        <DownloadBox className={classes.downloadBlock} />
      </div>

      <div className={classes.featurePage}>

        <div className={clsx(classes.featureBlock, classes.rl)}>
          <img
            src="screenshots/translation.png"
            alt="Controls"
            className={clsx(classes.featureImg)}
          />
          <div className={classes.featureTextBlock}>
            <Typography
              variant="h6"
              component="h3"
              className={classes.featureTitle}
            >
              Instant translation
            </Typography>
            <Typography className={classes.featureText}>
              Select all or a part of the subtitles to instantly translate it
              with your favorite dictionary.
            </Typography>
          </div>
        </div>

        <div className={classes.featureBlock}>
          <div className={classes.featureTextBlock}>
            <Typography
              variant="h6"
              component="h3"
              className={classes.featureTitle}
            >
              Instantly create Anki cards
            </Typography>
            <Typography className={classes.featureText}>
              Create{' '}
              <a
                href="https://apps.ankiweb.net/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Anki flashcards
              </a>{' '}
              on-the-fly using{' '}
              <a
                href="https://foosoft.net/projects/anki-connect/"
                target="_blank"
                rel="noopener noreferrer"
              >
                AnkiConnect
              </a>
              .
            </Typography>
            <Typography className={classes.ankiDescription}>
              <q>Anki is a program which makes remembering things easy. Because it&apos;s a lot more efficient than traditional study methods, you can either greatly decrease your time spent studying, or greatly increase the amount you learn.</q> — <a
                href="https://apps.ankiweb.net/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Anki
              </a>
            </Typography>
          </div>
          <img
            src="screenshots/anki.png"
            alt="Anki"
            className={clsx(classes.featureImg)}
          />
        </div>

        <div className={clsx(classes.featureBlock, classes.rl)}>
          <img
            src="screenshots/custom-translation-engine.png"
            alt="Translation"
            className={clsx(classes.featureImg)}
          />
          <div className={classes.featureTextBlock}>
            <Typography
              variant="h6"
              component="h3"
              className={classes.featureTitle}
            >
              Use Google Translate, Bing Translator, or any dictionary you like.
            </Typography>
            <Typography className={classes.featureText}>
              Any translator or dictionary based on URL parameters can be
              configured.
            </Typography>
          </div>
        </div>

        <div className={classes.featureBlock}>
          <div className={classes.featureTextBlock}>
            <Typography
              variant="h6"
              component="h3"
              className={classes.featureTitle}
            >
              Navigate by dialog
            </Typography>
            <Typography className={classes.featureText}>
              Repeat the current dialog as much as you need to listen exactly
              what the heck that guy&nbsp;is&nbsp;saying.
            </Typography>
          </div>
          <img
            src="screenshots/controls.png"
            alt="Controls"
            className={clsx(classes.featureImg)}
          />
        </div>

        <div className={clsx(classes.featureBlock, classes.rl)}>
          <img
            src="screenshots/two-subtitles.png"
            alt="Two subtitles tracks"
            className={clsx(classes.featureImg)}
          />
          <div className={classes.featureTextBlock}>
            <Typography
              variant="h6"
              component="h3"
              className={classes.featureTitle}
            >
              Display two subtitles tracks&nbsp;at&nbsp;once
            </Typography>
            <Typography className={classes.featureText}>
              Sometimes you need a bit more help.
            </Typography>
          </div>
        </div>

        <div className={classes.featureBlock}>
          <div className={classes.featureTextBlock}>
            <Typography
              variant="h6"
              component="h3"
              className={classes.featureTitle}
            >
              Show / Hide subtitles in one key press.
            </Typography>
            <Typography className={classes.featureText}>
              Sometimes you need to challenge yourself.
            </Typography>
          </div>
          <img
            src="screenshots/toggle-subtitles.png"
            alt="Controls"
            className={clsx(classes.featureImg)}
          />
        </div>

        <div className={clsx(classes.featureBlock, classes.rl)}>
          <img
            src="screenshots/settings-memory.png"
            alt="Two subtitles tracks"
            className={clsx(classes.featureImg)}
          />
          <div className={classes.featureTextBlock}>
            <Typography
              variant="h6"
              component="h3"
              className={classes.featureTitle}
            >
              Resume exactly where you were last time
            </Typography>
            <Typography className={classes.featureText}>
              Progression and subtitles settings are memorized for each movie.
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};
